"use client";
// ===================================================
// BranchTableOverlay — the vdev branch list as an actual table.
//
// WHY: גל, 06.09.2026: «אני רוצה לעשות ממש טבלה מסודרת שאני אבין מה אני
// רואה, ולא שורות ארוכות של מידע… העיקר שזה יראה כמו טבלה».
//
// הסבב הראשון נדחה: «זאת סתם רשימה עם המון מלל וקשה להבין מה זה מה».
// שתי הסיבות נמדדו, ושתיהן מתוקנות כאן:
//
//  1. שלושה תאים החזיקו **שתי שורות טקסט** זו מעל זו (נושא + שם הענף,
//     «לפני יומיים» + חותמת מלאה, פעמיים). שורה גבוהה עם שני משפטים היא
//     פסקה, לא תא. כאן כל תא הוא **שורה אחת**, והמידע המלא עבר ל-`title`.
//  2. ‏`GamTable` מצייר קווים אופקיים בלבד — אין מפרידים אנכיים ואין רוחב
//     קבוע לעמודה, ולכן העין לא רואה עמודות. שכבת ה-CSS למטה מוסיפה קו
//     אנכי בין תאים, פסים לסירוגין וכותרת דביקה, בהיקף המחלקה הזאת בלבד.
//
// המנוע אינו חדש (Rule 10). ‏`GamTable` הוא הטבלה של כל המערכת, ‏14 קבצים
// כבר מייבאים אותה, וממנה מגיעים בחינם החיפוש, המיון וסינוני השבבים.
//
// הצבעים כאן הם אסימונים בלבד — `var(--fg-muted)` ומחלקות תפקיד — ולא
// מחלקות פלטה גולמיות. שער הצבעים דורש מקובץ חדש אפס גולמיים (Rule 24).
// ===================================================
import { useEffect, useMemo, useState } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { Loader2, X } from "lucide-react";
import { GamTable, type GamColumnMeta } from "@/components/table/GamTable";
import {
  NO_APP,
  toRows,
  type BranchInfo,
  type BranchRow,
} from "@/lib/vdev/branch-rows";

const FG = { color: "var(--fg-default)" };
const MUTED = { color: "var(--fg-muted)" };
const FAINT = { color: "var(--fg-faint)" };

// מה שהופך את זה לטבלה ולא לרשימה: קו אנכי בין תאים, פסים לסירוגין
// וכותרת שנשארת במקום בגלילה. ‏`GamTable` משותפת ל-14 מסכים ולכן אינה
// נוגעה; ההיקף כאן הוא המחלקה הזאת בלבד.
const GRID_CSS = `
.branch-grid td, .branch-grid th {
  border-inline-start: 1px solid var(--border-hairline);
  white-space: nowrap;
}
.branch-grid td:first-child, .branch-grid th:first-child {
  border-inline-start: 0;
}
.branch-grid thead th {
  position: sticky;
  top: 0;
  z-index: 2;
  background: var(--bg-chrome);
  border-bottom: 1px solid var(--border-default);
}
.branch-grid tbody tr:nth-child(even) td {
  background: color-mix(in srgb, var(--fg-default) 3%, transparent);
}
.branch-grid tbody tr:hover td {
  background: color-mix(in srgb, var(--fg-default) 7%, transparent);
}
`;

// מצב הענף → מחלקת תפקיד. השבב לוקח את הצבע מן הטקסט (`current`), ולכן
// שורה אחת קובעת גם את המסגרת וגם את הרקע בלי שם צבע גולמי.
const STATE_ROLE: Record<string, string> = {
  בעבודה: "text-success",
  עומד: "text-warning",
  נטוש: "text-error",
};

function Tag({ text }: { text: string }) {
  const role = STATE_ROLE[text] ?? "text-primary";
  const isMerged = text === "מוזג";
  return (
    <span
      className={`inline-block rounded border border-current/30 bg-current/10 px-1.5 py-0.5 text-[10px] ${isMerged ? "" : role}`}
      style={isMerged ? FAINT : undefined}
    >
      {text}
    </span>
  );
}

/** תא של שורה אחת ברוחב קבוע — הרוחב הקבוע הוא מה שמיישר את העמודות. */
function Cell({
  width,
  title,
  ltr,
  style,
  children,
}: {
  width: string;
  title?: string;
  ltr?: boolean;
  style?: React.CSSProperties;
  children: React.ReactNode;
}) {
  return (
    <div
      className="truncate"
      dir={ltr ? "ltr" : undefined}
      title={title}
      style={{ width, ...style }}
    >
      {children}
    </div>
  );
}

export function BranchTableOverlay({
  branches,
  currentBranch,
  switching,
  onSwitch,
  onClose,
}: {
  branches: BranchInfo[];
  currentBranch?: string;
  switching: boolean;
  onSwitch: (name: string) => void;
  onClose: () => void;
}) {
  const [pm, setPm] = useState<Record<string, string>>({});

  useEffect(() => {
    const esc = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onClose]);

  // הסטטוס האנושי מגיע מ-PM. אם הקריאה נכשלת, הטבלה עדיין שלמה — היא נופלת
  // חזרה לסטטוס הנגזר מגיט, ולא נשארת ריקה.
  useEffect(() => {
    fetch("/api/vdev-switcher/branch-tasks")
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => d?.byBranch && setPm(d.byBranch as Record<string, string>))
      .catch(() => { /* אין PM — הסטטוס נשאר זה שנגזר מגיט */ });
  }, []);

  const rows = useMemo(() => toRows(branches, pm), [branches, pm]);

  const columns = useMemo<ColumnDef<BranchRow, unknown>[]>(
    () => [
      {
        id: "app",
        accessorKey: "app",
        header: "אפליקציה",
        meta: { filterable: true } satisfies GamColumnMeta,
        cell: ({ row }) => (
          <Cell width="8rem" style={row.original.app === NO_APP ? FAINT : FG}>
            {row.original.app}
          </Cell>
        ),
      },
      {
        id: "kind",
        accessorKey: "kind",
        header: "סוג",
        meta: { filterable: true } satisfies GamColumnMeta,
        cell: ({ row }) => (
          <Cell width="4.5rem" ltr style={MUTED}>{row.original.kind}</Cell>
        ),
      },
      {
        id: "pmStatus",
        accessorKey: "pmStatus",
        header: "סטטוס",
        meta: { filterable: true } satisfies GamColumnMeta,
        cell: ({ row }) => (
          <div style={{ width: "6rem" }}><Tag text={row.original.pmStatus} /></div>
        ),
      },
      {
        id: "short",
        accessorKey: "short",
        header: "הענף",
        cell: ({ row }) => (
          <Cell width="14rem" ltr title={row.original.name} style={FG}>
            {row.original.short}
          </Cell>
        ),
      },
      {
        id: "subject",
        accessorKey: "subject",
        header: "מה יש בענף",
        cell: ({ row }) => (
          <Cell
            width="26rem"
            title={row.original.subject || undefined}
            style={row.original.subject ? MUTED : FAINT}
          >
            {row.original.subject || "אין תיאור"}
          </Cell>
        ),
      },
      {
        id: "editedAt",
        accessorKey: "editedAt",
        header: "נערך",
        cell: ({ row }) => (
          // חותמת מלאה ב-`title`. שתי שורות בתא הן מה שהפך את הסבב הקודם
          // לרשימה, ולכן התאריך המדויק יושב בריחוף ולא מתחת לטקסט.
          <Cell width="7rem" title={row.original.editedAt} style={MUTED}>
            {row.original.editedAgo || "—"}
          </Cell>
        ),
      },
      {
        id: "go",
        header: "",
        enableSorting: false,
        cell: ({ row }) => {
          const here = row.original.name === currentBranch;
          return (
            <button
              type="button"
              disabled={switching || here}
              onClick={(e) => { e.stopPropagation(); onSwitch(row.original.name); }}
              className="btn-ghost flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium disabled:cursor-not-allowed disabled:opacity-30"
            >
              {switching ? <Loader2 size={10} className="animate-spin" /> : null}
              {here ? "כאן" : "עבור"}
            </button>
          );
        },
      },
    ],
    [currentBranch, switching, onSwitch],
  );

  return (
    <div
      dir="rtl"
      onClick={onClose}
      className="fixed inset-x-0 bottom-0 top-9 z-[57] flex justify-center overflow-y-auto bg-black/60 backdrop-blur-sm"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="card my-4 h-fit w-full max-w-6xl p-4 shadow-2xl"
        style={{ background: "var(--bg-app)" }}
      >
        <style>{GRID_CSS}</style>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-base font-semibold" style={FG}>
            🌿 ענפים ב-vdev
            <span className="mr-2 text-[11px] font-normal" style={FAINT}>{rows.length} ענפים</span>
          </h2>
          <button type="button" onClick={onClose} className="btn-ghost flex items-center gap-1 px-2 py-0.5 text-[11px]">
            <X size={12} /> סגור
          </button>
        </div>
        <div className="branch-grid">
          <GamTable
            data={rows}
            columns={columns}
            getRowId={(r) => r.name}
            searchPlaceholder="חיפוש בענפים…"
            pageSize={25}
            emptyText="אין ענפים — המתג אינו זמין"
          />
        </div>
        {/* גל ביקש «מתי זה נפתח». המתג ב-VPS מחזיר תאריך אחד בלבד, של
            הקומיט האחרון, ולכן אין ממה לגזור את זמן הפתיחה. עמודה שכל
            שורותיה «לא נמדד» היא בדיוק הרעש שנדחה, ולכן זו הערה ולא עמודה. */}
        <p className="mt-2 text-[10px]" style={FAINT}>
          תאריך פתיחת הענף אינו נמדד — המתג מחזיר רק את זמן הקומיט האחרון
        </p>
      </div>
    </div>
  );
}
